import React, { useMemo, useState } from "react";
import { Card, Drawer, Table, message } from "antd";
import { MDBCol, MDBRow } from "mdbreact";
import { Text } from "react-font";
import { Button, SearchInput } from "evergreen-ui";
import Firebase from "../../Firebase/Firebase";
import { TableExport } from "tableexport";
import FireFetch from "../../Firebase/FireFetch";
import { useListVals } from "../../Firebase/databaseHooks";
import ViewConsents from "../../Modals/Feedback/ViewConsents";
import { getDatabase, ref } from "firebase/database";
import { useAuth } from "../../AuthContext/AuthContext";
import TableActionMenu from "../../Common/TableActionMenu";
import { getUserName, useMergedListVals } from "../../Firebase/dataCompat";
import {
	formatConsentDate,
	getConsentEndpoint,
	matchesConsentSearch,
	normalizeConsentRecord,
} from "./consentCompat";

const db = getDatabase(Firebase);
const compareText = (left, right) => String(left || "").localeCompare(String(right || ""));

const Consents = () => {
	const { canAccessModule } = useAuth();
	const canDeleteConsent = canAccessModule("CONSENTS", "delete");
	const [searchValue, setSearchValue] = useState("");
	const [selectedConsent, setSelectedConsent] = useState(null);
	const [viewModal, setViewModal] = useState(false);
	const [organizationArray, organizationsLoading] = useListVals(ref(db, "talmidsdb/organisations"));
	const [communityArray, communitiesLoading] = useListVals(ref(db, "talmidsdb/communities"));
	const [userArray, usersLoading] = useListVals(ref(db, "talmidsdb/users"));
	const [consentArray, consentsLoading] = useMergedListVals(
		"talmidsdb/consents",
		"talmidsdb/ConsentRequests"
	);
	const loading = consentsLoading || organizationsLoading || communitiesLoading || usersLoading;

	const normalizedConsents = useMemo(
		() => consentArray.map(normalizeConsentRecord),
		[consentArray]
	);

	const rows = useMemo(() => normalizedConsents.map((consent) => {
		const community = communityArray.find((item) =>
			item.id === consent.communityID || item.communityID === consent.communityID
		);
		const organisation = organizationArray.find((item) =>
			item.id === consent.organisationID ||
			item.orgID === consent.organisationID ||
			item.organisationID === consent.organisationID
		);
		const row = {
			key: consent.consentRequestID || consent.id,
			consentType: consent.consentTypeLabel,
			name: consent.name,
			consentTo: consent.consentTo,
			notes: consent.notes || "-",
			community: community?.name || community?.communityName || "Unknown community",
			user: getUserName(userArray, consent.submittedByID, "Unknown user"),
			organisation: organisation?.name || "Unknown organization",
			time: formatConsentDate(consent.date),
			phone: consent.phone || "-",
			gender: consent.gender || "-",
			consent,
		};
		return {
			...row,
			action: (
				<TableActionMenu
					items={[
						{
							key: "view",
							label: "View",
							onClick: () => {
								setSelectedConsent(consent);
								setViewModal(true);
							},
						},
						canDeleteConsent && {
							key: "delete",
							label: "Delete",
							danger: true,
							onClick: async () => {
								if (!confirm("Are you sure you want to delete Consent?")) return;
								try {
									const result = await FireFetch.DeleteFromDB(
										getConsentEndpoint(consent),
										consent.consentRequestID || consent.id
									);
									if (result !== "success") throw new Error("Unable to delete");
									FireFetch.addLog("consents", consent.consentRequestID || consent.id, "Delete");
									message.success("Consent deleted");
								} catch (error) {
									message.error(error?.message || "Unable to delete consent");
								}
							},
						},
					]}
				/>
			),
		};
	}), [normalizedConsents, communityArray, organizationArray, userArray, canDeleteConsent]);

	const filteredRows = useMemo(
		() => rows.filter((row) => matchesConsentSearch(row, searchValue)),
		[rows, searchValue]
	);

	const columns = [
		{ title: "Consent", dataIndex: "consentType", key: "consentType", sorter: (a, b) => compareText(a.consentType, b.consentType) },
		{ title: "Name", dataIndex: "name", key: "name", sorter: (a, b) => compareText(a.name, b.name) },
		{ title: "Consent To", dataIndex: "consentTo", key: "consentTo", sorter: (a, b) => compareText(a.consentTo, b.consentTo) },
		{ title: "Notes", dataIndex: "notes", key: "notes", sorter: (a, b) => compareText(a.notes, b.notes) },
		{ title: "Community", dataIndex: "community", key: "community", sorter: (a, b) => compareText(a.community, b.community) },
		{ title: "Submitted By", dataIndex: "user", key: "user", sorter: (a, b) => compareText(a.user, b.user) },
		{ title: "Organization", dataIndex: "organisation", key: "organisation", sorter: (a, b) => compareText(a.organisation, b.organisation), responsive: ["sm"] },
		{ title: "Time", dataIndex: "time", key: "time", sorter: (a, b) => compareText(a.time, b.time), responsive: ["sm"] },
		{ title: "Action", dataIndex: "action", key: "action" },
	];

	const exportRows = () => {
		const exportData = filteredRows.map(({ action, consent, ...row }) => ({ ...row }));
		FireFetch.exportXL(TableExport(document.getElementById("dataTable"), {
			filename: "Consents",
			exportButtons: false,
			sheetname: "Consents",
		}), exportData);
	};

	return (
		<div className="d-flex justify-content-center align-items-center pt-4 mt-4">
			<Drawer
				title="View Consent"
				width={800}
				maskClosable
				className="font-Nunito"
				placement="right"
				open={viewModal}
				onClose={() => setViewModal(false)}
			>
				<ViewConsents consent={selectedConsent} />
			</Drawer>
			<MDBRow className="w-100">
				<MDBCol md={12}>
					<Card className="w-100">
						<MDBRow><MDBCol md={12}>
							<div className="d-flex flex-row justify-content-between">
								<h5 className="font-weight-bold" style={{ color: "#51195D" }}><Text family="Nunito">Consents</Text></h5>
								<div className="d-flex flex-row">
									<SearchInput
										placeholder="Filter consents"
										onChange={(event) => setSearchValue(event.target.value)}
										value={searchValue}
									/>
									<Button intent="primary" appearance="primary" onClick={exportRows} className="d-inline float-right">Print</Button>
								</div>
							</div>
						</MDBCol></MDBRow>
					</Card>
					<Card>
						<Table
							className="custom-table"
							id="dataTable"
							loading={loading}
							pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ["100", "200", "300"] }}
							style={{ overflow: "auto" }}
							columns={columns}
							dataSource={filteredRows}
							rowKey="key"
						/>
					</Card>
				</MDBCol>
			</MDBRow>
		</div>
	);
};

export default Consents;
